import { AmplifyUser } from '@aws-amplify/ui';
import { Auth } from 'aws-amplify';
import { isUserRole, UserRole } from '../types/backendType';

export class LoginService {
  private static instance: LoginService | undefined;

  private amplifyUser: AmplifyUser | undefined;

  static getInstance(): LoginService {
    if (LoginService.instance === undefined) {
      LoginService.instance = new LoginService();
    }
    return LoginService.instance;
  }

  private constructor() {
    // private constructor
  }

  getUserId() {
    return this.amplifyUser?.attributes?.preferred_username;
  }

  // Returns the user's email from the Cognito `email` attribute. Distinct from
  // getUserId(): for some accounts `preferred_username` is the Cognito sub
  // (UUID) rather than the email, so anything user-facing (Contact Us
  // diagnostics, etc.) must use this instead of getUserId().
  getEmail() {
    return this.amplifyUser?.attributes?.email;
  }

  getUserRole(): UserRole | undefined {
    const role = this.amplifyUser?.attributes?.['custom:v2_role'];
    if (role && isUserRole(role)) {
      return role;
    }
  }

  getTenantId(): string | undefined {
    return this.amplifyUser?.attributes?.['custom:tenant_id'];
  }

  async getToken() {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  }

  setAmplifyUser(amplifyUser: AmplifyUser) {
    this.amplifyUser = amplifyUser;
  }

  /**
   * Changes the signed-in user's own password directly against Cognito.
   *
   * Note this deliberately does NOT use the cached `amplifyUser`: that is the
   * `AmplifyUser` handed over by the Authenticator, while `changePassword`
   * needs a `CognitoUser` carrying a live `signInUserSession`.
   * `currentAuthenticatedUser()` returns one and refreshes the session as a
   * side effect, which is what we want before a credential operation.
   *
   * Cognito's ChangePassword revokes nothing — the current session and every
   * other device's refresh token stay valid. Callers wanting to evict other
   * sessions must additionally call `Auth.signOut({ global: true })`.
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(user, oldPassword, newPassword);
  }
}

export type ChangePasswordErrorCode =
  | 'WRONG_CURRENT_PASSWORD'
  | 'SESSION_INVALID'
  | 'POLICY_REJECTED'
  | 'RATE_LIMITED'
  | 'RESET_REQUIRED'
  | 'NETWORK'
  | 'UNKNOWN';

export type ChangePasswordFailure = {
  /** Stable token, safe for telemetry. Never a raw Cognito string. */
  code: ChangePasswordErrorCode;
  /** User-facing copy. Always a literal from this function. */
  message: string;
};

/**
 * Maps a `changePassword` rejection onto a fixed code + friendly copy.
 *
 * This is the ONLY thing callers may read off the caught error. Every branch
 * returns a literal and there is no interpolation path, so an unrecognised
 * Cognito exception degrades to UNKNOWN rather than leaking its raw message to
 * the user. That matters most for a wrong current password, where Cognito says
 * "Incorrect username or password." — misleading here, since no username is
 * involved. Same technique as the MFA error mapping in `AppWithAuth.tsx`.
 */
export function classifyChangePasswordError(error: unknown): ChangePasswordFailure {
  const err = error as { code?: string; name?: string; message?: string };
  // Amplify v5 populates `code` and `name` inconsistently across error shapes.
  const code = err?.code ?? err?.name ?? '';
  // Lowercased ONLY to discriminate between branches — never displayed.
  const raw = (err?.message ?? '').toLowerCase();

  switch (code) {
    case 'NotAuthorizedException':
      // The same exception covers both a bad current password and a dead
      // session; the message is the only discriminator Cognito gives us.
      if (raw.includes('expired') || raw.includes('revoked') || raw.includes('access token')) {
        return {
          code: 'SESSION_INVALID',
          message: 'Your session has expired. Please sign in again and retry.',
        };
      }
      return {
        code: 'WRONG_CURRENT_PASSWORD',
        message: 'Your current password is incorrect.',
      };

    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      return {
        code: 'POLICY_REJECTED',
        // Phrased without an apostrophe on purpose: Prettier rewrites a string
        // containing one to double quotes, which the `quotes` ESLint rule then
        // rejects — an unwinnable loop.
        message:
          'That password does not meet the requirements. Use at least 12 ' +
          'characters with an uppercase letter, a lowercase letter, a number ' +
          'and a special character, and no spaces at the start or end.',
      };

    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return {
        code: 'RATE_LIMITED',
        message: 'Too many attempts. Please wait a few minutes and try again.',
      };

    case 'PasswordResetRequiredException':
      return {
        code: 'RESET_REQUIRED',
        message:
          'Your password must be reset. Sign out and use Forgot password on ' +
          'the sign-in page.',
      };

    case 'UserNotFoundException':
    case 'UserNotConfirmedException':
      return {
        code: 'SESSION_INVALID',
        message: 'Your session is no longer valid. Please sign in again.',
      };

    default:
      break;
  }

  // `currentAuthenticatedUser()` rejects with a bare string or a plain Error
  // when there is no session at all.
  if (raw.includes('not authenticated') || raw.includes('no current user')) {
    return {
      code: 'SESSION_INVALID',
      message: 'Your session is no longer valid. Please sign in again.',
    };
  }

  // Only claim a network problem on an actual network signal. An unrecognised
  // error with no code is NOT evidence of one — telling the user to check their
  // connection when the real fault is something else just sends them chasing it.
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline || raw.includes('network') || raw.includes('failed to fetch')) {
    return {
      code: 'NETWORK',
      message: 'Network problem. Check your connection and try again.',
    };
  }

  return {
    code: 'UNKNOWN',
    message: 'We could not change your password. Please try again.',
  };
}
